// ⚠️  GENERATED FILE — DO NOT EDIT.
//
// Copied verbatim from packages/design/src/drift-color.ts by
// packages/design/scripts/sync-drift-color.mjs. Edit the canonical file and run
//   pnpm --filter @tempest/design-tokens sync:drift
// packages/design/scripts/drift-color.parity.test.mjs fails if this drifts.

/**
 * drift-color — the ONE implementation of the drift accent colour maths.
 *
 * ── Why this file exists (TEM-1192) ───────────────────────────────────────
 * `--drift` / `--glow` are the per-album accent. They are written with
 * `root.style.setProperty()` on `document.documentElement`, and an inline style
 * outranks EVERY CSS selector — including `[data-theme="cinematic-light"]`. So
 * no token block can ever correct them: once drift runs, the theme has lost.
 * The accent therefore has to be computed, in JS, against the ground it will
 * actually be rendered on, at the moment it is applied.
 *
 * The maths had been copied into five modules and had already diverged (Radio's
 * `darken` used the inverse parameter convention and dropped the green bias —
 * see `DARKEN_G` below). Five copies of colour maths is how they diverged in
 * the first place, so this file is now the single source of truth: it is
 * COPIED VERBATIM into each app by `packages/design/scripts/sync-drift-color.mjs`
 * and the copies are asserted byte-identical by `drift-color.parity.test.mjs`.
 * Edit this file, then run `pnpm --filter @tempest/design-tokens sync:drift`.
 *
 * Deliberately dependency-free and DOM-optional so it runs unchanged in
 * `node --test`, vitest/jsdom, CRA/jest and four different bundlers.
 */

export interface Rgb { r: number; g: number; b: number }

/** Neutral Storm-Blue used when no album colour is available. */
export const DRIFT_SEED = '#3B82F6';

/**
 * The ground each theme renders `--drift` against.
 *
 * Which surface is "worst case" INVERTS between the themes, because the accent
 * inverts with it. Measured 2026-08-09 over all 15,659,865 reachable colours:
 *
 *   dark  — accent is light-on-dark, so the LIGHTEST surface is worst:
 *           --ground #08090d 37.7% fail 4.5:1 · --surface-3 #232734 53.4% fail
 *   light — accent is dark-on-light, so the DARKEST surface is worst:
 *           --ground #e8eaef 72.0% fail 4.5:1 · --surface-3 #ffffff 63.2% fail
 *
 * Both bounds below are `--ground`. For light that IS the worst case. For dark
 * it is NOT — clamping dark against `--surface-3` would be strictly stronger,
 * but it would also re-tint every album accent in the shipped dark theme, which
 * is a product decision and not this ticket's. Phase 0 chose `--ground`; this
 * ticket keeps dark byte-identical to it and fixes light. Raising the dark
 * bound later is a one-line change here.
 */
export const DARK_GROUND = '#08090d';
export const LIGHT_GROUND = '#e8eaef';

/** Themes whose ground is not the default dark one. Anything unlisted — the five
 *  retired pre-v3 skins included — is dark, which is also the safe default. */
export const THEME_GROUNDS: Readonly<Record<string, string>> = Object.freeze({
  'cinematic': DARK_GROUND,
  'cinematic-light': LIGHT_GROUND,
});

export const DEFAULT_GROUND = DARK_GROUND;

/** Default blend factor for darken()/lighten(). Carried from the mockup drift(). */
export const GLOW_F = 0.34;

// Per-channel bias. darken() pulls toward a BLUE-black: on the colour axis blue
// is attenuated least (factor 1.0) and lifted (+6), red most — so blue is the
// channel that SURVIVES the shadow, and the result reads cool.
const DARKEN_R = 0.90;
const DARKEN_G = 0.95;   // Radio's copy omitted this, so its GLOW_SEED #12245a
                         // carried g=36 where the contract says 42.
const DARKEN_B = 1.00;
const DARKEN_B_LIFT = 6;

// lighten() mirrors that INTENT onto the distance-from-white axis, and the role
// of red and blue therefore SWAPS. "Cool" always means blue is the highest
// channel; in a highlight that means blue must be the channel NEAREST white, so
// blue's distance is attenuated most (0.90) and red's least (1.00) — the exact
// opposite ranking to darken's. Mirroring the constants position-for-position
// instead gives blue the largest remaining distance, i.e. the LOWEST blue value,
// which renders a warm cream: lighten('#1D4ED8') = rgb(186,198,236) and
// lighten('#ffffff') = rgb(255,255,249). Both are the bug, not the contract.
const LIGHTEN_R = 1.00;
const LIGHTEN_G = 0.95;
const LIGHTEN_B = 0.90;
const LIGHTEN_B_LIFT = 6;

const clampByte = (n: number): number => Math.max(0, Math.min(255, Math.round(n)));

export function parseHex(hex: string): Rgb {
  let h = String(hex).trim().replace(/^#/, '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return parseHex(DRIFT_SEED);
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function toHex({ r, g, b }: Rgb): string {
  const h = (n: number) => clampByte(n).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

/** WCAG 2.1 relative luminance — linearises each channel. This is the one that
 *  governs legibility. https://www.w3.org/TR/WCAG21/#dfn-relative-luminance */
function channelLin(c: number): number {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance({ r, g, b }: Rgb): number {
  return 0.2126 * channelLin(r) + 0.7152 * channelLin(g) + 0.0722 * channelLin(b);
}

export function contrast(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Contrast between two colour STRINGS (hex or `rgb(r,g,b)`). */
export function contrastHex(a: string, b: string): number {
  return contrast(parseColor(a), parseColor(b));
}

/** Parse hex OR the `rgb(r,g,b)` form this module emits. `null` when the input
 *  is not a colour at all (e.g. a `var(...)` that never resolved) — callers that
 *  need a value use `parseColor`, callers that need to KNOW use this. */
export function tryParseColor(input: string): Rgb | null {
  const s = String(input).trim();
  const m = /^rgba?\(([^)]+)\)$/i.exec(s);
  if (m) {
    const p = m[1].split(/[,/\s]+/).filter(Boolean).map(Number);
    if (p.length >= 3 && p.slice(0, 3).every((n) => Number.isFinite(n))) {
      return { r: clampByte(p[0]), g: clampByte(p[1]), b: clampByte(p[2]) };
    }
    return null;
  }
  const h = s.replace(/^#/, '');
  if ((h.length === 3 || h.length === 6) && !/[^0-9a-fA-F]/.test(h)) return parseHex(s);
  return null;
}

/** Parse hex OR `rgb(r,g,b)`. Falls back to the seed on anything unrecognised. */
export function parseColor(input: string): Rgb {
  return tryParseColor(input) ?? parseHex(DRIFT_SEED);
}

/** True when `background` is light enough that text on it must go DARKER. */
export function isLightBackground(background: string): boolean {
  return relativeLuminance(parseColor(background)) >= 0.5;
}

/** The ground for a `data-theme` value. Unknown/absent → the dark default. */
export function groundForTheme(themeId: string | null | undefined): string {
  if (!themeId) return DEFAULT_GROUND;
  return THEME_GROUNDS[themeId] ?? DEFAULT_GROUND;
}

/**
 * The ground the document is CURRENTLY painting.
 *
 * Prefers the live computed `--ground`, so a theme this module has never heard
 * of still gets a correct accent; falls back to the `data-theme` map when the
 * custom property is unresolvable (jsdom, boot before the theme is stamped).
 */
export function currentGround(): string {
  if (typeof document === 'undefined' || !document.documentElement) return DEFAULT_GROUND;
  const root = document.documentElement;
  if (typeof getComputedStyle === 'function') {
    try {
      const parsed = tryParseColor(getComputedStyle(root).getPropertyValue('--ground'));
      if (parsed) return toHex(parsed);
    } catch {
      /* non-browser / partial CSSOM — fall through to the map */
    }
  }
  return groundForTheme(root.getAttribute('data-theme'));
}

/**
 * Darken toward blue-black. `f` is the RETAINED factor (0 = black, 1 = as-is) —
 * NOT the amount removed. Radio's copy used the inverse convention with
 * `darken(hex, 0.72)`, i.e. a retained 0.28, and omitted the green bias; both
 * are converged here. Emits the compact `rgb(r,g,b)` form the dark token
 * `--glow: rgb(18, 42, 90)` was authored from.
 */
export function darken(hex: string, f: number = GLOW_F): string {
  const { r, g, b } = parseColor(hex);
  return `rgb(${clampByte(r * f * DARKEN_R)},${clampByte(g * f * DARKEN_G)},${clampByte(b * f * DARKEN_B + DARKEN_B_LIFT)})`;
}

/**
 * Lighten toward blue-white — darken()'s intent mirrored onto the
 * distance-from-white axis (see the LIGHTEN_* constants for why the red/blue
 * roles swap rather than transpose).
 *
 * This is the light-theme `--glow`, and it is the whole reason the ticket
 * needed a companion function: on a light ground darken() produces a near-black
 * smear that reads as dirt rather than atmosphere.
 *
 * Unlike darken(), this is monotonic at both extremes — white stays white,
 * because the blue lift SUBTRACTS distance and is floored at zero.
 */
export function lighten(hex: string, f: number = GLOW_F): string {
  const { r, g, b } = parseColor(hex);
  const up = (c: number, k: number, lift = 0) =>
    255 - clampByte(Math.max(0, (255 - c) * f * k - lift));
  return `rgb(${up(r, LIGHTEN_R)},${up(g, LIGHTEN_G)},${up(b, LIGHTEN_B, LIGHTEN_B_LIFT)})`;
}

/** The ambient glow for `hex` on `background` — darken on a dark ground,
 *  lighten on a light one. THE theme-aware half of the accent. */
export function glowFor(hex: string, background: string = DEFAULT_GROUND): string {
  return isLightBackground(background) ? lighten(hex) : darken(hex);
}

/**
 * Move `color` along its own hue until it clears `target` contrast against
 * `background`. Two-sided: lightens on a dark ground, darkens on a light one,
 * so the same function serves both themes.
 */
export function ensureReadable(color: string, background: string, target = 4.5): string {
  const bg = parseColor(background);
  const fg = parseColor(color);
  // Return the INPUT string untouched when it already passes — normalising here
  // would silently rewrite every caller's hex casing for no benefit.
  if (contrast(fg, bg) >= target) return color;

  // Decide direction once, from the background, so we never cross over mid-way.
  const toward: Rgb = relativeLuminance(bg) < 0.5
    ? { r: 255, g: 255, b: 255 }
    : { r: 0, g: 0, b: 0 };
  const round = ({ r, g, b }: Rgb): Rgb => ({ r: Math.round(r), g: Math.round(g), b: Math.round(b) });

  // Binary search the blend factor: monotonic in contrast, so 20 iterations is
  // exact. The predicate is evaluated on the ROUNDED colour, so the value we
  // return is the one that was actually measured — rounding after the search
  // could drop it back under the bar by up to half a channel step.
  //
  // `best` seeds at the endpoint (pure white / pure black). If even that cannot
  // reach `target` — a mid-grey background — the loop never improves on it and
  // we return the most legible colour that exists, rather than throwing.
  let lo = 0;
  let hi = 1;
  let best = round(toward);
  for (let i = 0; i < 20; i++) {
    const t = (lo + hi) / 2;
    const mix = round({
      r: fg.r + (toward.r - fg.r) * t,
      g: fg.g + (toward.g - fg.g) * t,
      b: fg.b + (toward.b - fg.b) * t,
    });
    if (contrast(mix, bg) >= target) { best = mix; hi = t; } else { lo = t; }
  }
  return toHex(best);
}

/**
 * The whole accent for one album colour on one ground: a `--drift` guaranteed
 * legible as text, and its matching `--glow`.
 *
 * The glow is derived from the READABLE drift, not the raw input, so the two
 * channels stay in the same hue family after a clamp.
 */
export function driftFor(hex: string, background: string = DEFAULT_GROUND): { drift: string; glow: string } {
  const drift = ensureReadable(hex, background);
  return { drift, glow: glowFor(drift, background) };
}

// ── the DOM controller ────────────────────────────────────────────────────
//
// This lives here rather than in each app's drift.ts for the same reason the
// maths does: it is the part that has to STAY in step with the theme, and five
// hand-written copies of "recompute on theme change" is five chances to forget.

export const DRIFT_T = '.7s cubic-bezier(.4,0,.2,1)';
export const INSTANT_T = '1ms';

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** The RAW album colours last applied, per channel. Kept so a theme change can
 *  recompute from the original input rather than re-clamping an already-clamped
 *  value, which would ratchet the accent further every toggle. */
const seeds: { drift: string | null; album: string | null } = { drift: null, album: null };

let themeObserver: MutationObserver | null = null;

const VARS = {
  drift: ['--drift', '--glow'],
  album: ['--drift-album', '--glow-album'],
} as const;

/**
 * The album accent's theme-INVARIANT companion pair (TEM-1244).
 *
 * Record's Edit stage is a declared dark island: `.edit-stage-v3` pins its whole
 * ramp (ground/surface/ink/dim/faint/border) as local custom properties in BOTH
 * themes. An accent clamped against `currentGround()` — the DOCUMENT's wall —
 * is wrong inside it under cinematic-light: ensureReadable darkens the accent
 * for a light wall it will never be painted on, and the island's near-black ink
 * lands at 3.67:1 on typical covers, bottoming near 1.00:1 on dark-dominant art.
 *
 * So every album write also emits the SAME seed clamped against DARK_GROUND,
 * unconditionally. The island consumes `var(--drift-album-dark, …)` and nothing
 * that follows the theme ever crosses its boundary. Structurally safe ink:
 * `#06070a` is darker than DARK_GROUND `#08090d`, so any accent that clears
 * 4.5:1 against the ground clears 4.5:1 against the ink a fortiori — the
 * 636,056-seed sweep in drift-color.test.mjs measures this (`worst.dark`).
 */
const ALBUM_DARK_VARS = ['--drift-album-dark', '--glow-album-dark'] as const;

function write(channel: 'drift' | 'album', hex: string, ground: string): void {
  const root = document.documentElement;
  const { drift, glow } = driftFor(hex, ground);
  const [dv, gv] = VARS[channel];
  root.style.setProperty(dv, drift);
  root.style.setProperty(gv, glow);
  if (channel === 'album') {
    // Theme-invariant by construction: same seed, fixed dark wall. Recomputing
    // on refreshDrift() is redundant but idempotent, and keeping it inside
    // write() means no caller can update one pair and not the other.
    const dark = driftFor(hex, DARK_GROUND);
    const [ddv, dgv] = ALBUM_DARK_VARS;
    root.style.setProperty(ddv, dark.drift);
    root.style.setProperty(dgv, dark.glow);
  }
}

/**
 * Recompute every live accent against the CURRENT ground.
 *
 * `--drift`/`--glow` are inline styles on <html>, so they outrank the theme
 * blocks — switching theme cannot fix them by cascade, and without this the
 * accent stays stale (and, crossing dark→light, illegible) until the next track.
 */
export function refreshDrift(): void {
  if (typeof document === 'undefined' || !document.documentElement) return;
  const ground = currentGround();
  if (seeds.drift !== null) write('drift', seeds.drift, ground);
  if (seeds.album !== null) write('album', seeds.album, ground);
}

/** Watch <html data-theme> and re-derive on change. Installed lazily on first
 *  apply so a module import never touches the DOM. */
function ensureThemeObserver(): void {
  if (themeObserver || typeof MutationObserver === 'undefined') return;
  themeObserver = new MutationObserver(refreshDrift);
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });
}

/** Owns `--drift` / `--glow` on document.documentElement. Colour ALWAYS updates
 *  (it is a semantic); `--t` collapses to instant under reduced motion. */
export function applyDrift(hex: string, { transition = true } = {}): void {
  if (typeof document === 'undefined' || !document.documentElement) return;
  seeds.drift = hex;
  if (transition) {
    document.documentElement.style.setProperty('--t', prefersReducedMotion() ? INSTANT_T : DRIFT_T);
  }
  write('drift', hex, currentGround());
  ensureThemeObserver();
}

/** The Record-workflow album bleed: `--drift-album` / `--glow-album`, which the
 *  Record stages read as `var(--drift-album, …)` so the capture workflow can take
 *  on the record being set up without disturbing the app-wide play accent.
 *  Also emits `--drift-album-dark` / `--glow-album-dark` — see ALBUM_DARK_VARS. */
export function applyAlbumDrift(hex: string, { transition = true } = {}): void {
  if (typeof document === 'undefined' || !document.documentElement) return;
  seeds.album = hex;
  if (transition) {
    document.documentElement.style.setProperty('--t', prefersReducedMotion() ? INSTANT_T : DRIFT_T);
  }
  write('album', hex, currentGround());
  ensureThemeObserver();
}

export function clearAlbumDrift(): void {
  if (typeof document === 'undefined' || !document.documentElement) return;
  seeds.album = null;
  for (const v of [...VARS.album, ...ALBUM_DARK_VARS]) {
    document.documentElement.style.removeProperty(v);
  }
}

/** Test hook — drops the remembered seeds and disconnects the observer so each
 *  case starts from a known state. Not used in production code. */
export function resetDriftState(): void {
  seeds.drift = null;
  seeds.album = null;
  themeObserver?.disconnect();
  themeObserver = null;
}
